// useEventStream — SSE hook for the RMINT projector event bus.
//
// Phase 6 §6.5 introduced this hook (one EventSource per call site).
// Phase 7 §7.6 refactor:
//   1. SINGLE GLOBAL EventSource — all useEventStream() consumers
//      on a page share ONE connection. The page-level SSEManager
//      fans out events client-side to per-subscriber filters. This
//      kills the per-card connection cost noted in the Phase 6
//      deferral.
//   2. PRIVY COOKIE AUTH — EventSource sends the `privy-token`
//      cookie automatically on same-origin requests. The projector's
//      `accessTokenFromRequest` reads the cookie when no
//      Authorization header is present. The previous `?token=`
//      query-param fallback is REMOVED.
//
// Server-side filtering is intentionally dropped here too: with one
// global stream serving N subscribers, we can't match the server's
// `?entity=` filter to any single subscriber. ALL accessible events
// flow through the stream; filters apply on the client. At v7's
// event-volume scale (<100 events/min) this is well within budget.
//
// Polling fallback (§6.5 founder-approved default) remains a TODO
// for /events/recent when that endpoint lands. Today the manager
// reconnects with exponential backoff on error.

import { useEffect, useRef, useState } from 'react';
import { env } from './env';

// ─── Public types ─────────────────────────────────────────────────

export type ProjectorEvent = {
  type: string;       // e.g. 'kyc.submitted'
  entity: string;     // e.g. 'subscription:sub-01ABC'
  timestamp: string;  // ISO
  eventId: string;
  entityType: string;
  entityId: string;
};

export type EventStreamFilter = {
  /** Exact match on event.entity, e.g. 'subscription:sub-01ABC' */
  entity?: string;
  /** Prefix match on event.type, e.g. 'kyc.' matches 'kyc.submitted' */
  typePrefix?: string;
};

export type EventStreamStatus = 'connecting' | 'open' | 'closed' | 'error';

export type UseEventStreamResult = {
  events: ProjectorEvent[];
  status: EventStreamStatus;
  reconnect: () => void;
};

// ─── Constants ────────────────────────────────────────────────────

const MAX_EVENTS = 100;   // ring-buffer cap per subscriber
const BACKOFF_INITIAL_MS = 1000;
const BACKOFF_MAX_MS = 30_000;

// ─── Module-private SSE manager ───────────────────────────────────
//
// Singleton inside the browser tab — one EventSource for all
// useEventStream() consumers. Public API: subscribe/unsubscribe.
// Connection lifecycle:
//   - First subscriber → open the EventSource.
//   - Last subscriber unsubscribes → close after a short grace
//     period (so a fast remount doesn't churn).

type Subscriber = {
  id: number;
  filter: EventStreamFilter;
  onEvent: (e: ProjectorEvent) => void;
  onStatus: (s: EventStreamStatus) => void;
};

let _es: EventSource | null = null;
let _status: EventStreamStatus = 'closed';
let _backoffMs = BACKOFF_INITIAL_MS;
let _retryTimer: ReturnType<typeof setTimeout> | null = null;
let _closeTimer: ReturnType<typeof setTimeout> | null = null;
let _nextSubscriberId = 0;
const _subscribers = new Set<Subscriber>();

function buildUrl(): string {
  return env.apiBaseUrl ? `${env.apiBaseUrl}/events/stream` : '/events/stream';
}

function setStatus(next: EventStreamStatus): void {
  _status = next;
  for (const sub of _subscribers) sub.onStatus(next);
}

function matches(evt: ProjectorEvent, filter: EventStreamFilter): boolean {
  if (filter.typePrefix && !evt.type.startsWith(filter.typePrefix)) return false;
  if (filter.entity && evt.entity !== filter.entity) return false;
  return true;
}

function fanOut(evt: ProjectorEvent): void {
  for (const sub of _subscribers) {
    if (matches(evt, sub.filter)) sub.onEvent(evt);
  }
}

function open(): void {
  if (_es) return;
  setStatus('connecting');
  // EventSource auto-sends cookies on same-origin requests; the
  // privy-token cookie carries the bearer auth. Cross-origin would
  // require `withCredentials: true` AND a CORS allow-credentials
  // response — we run same-origin in production (projector serves
  // SPA + API together; see ARCHITECTURE Phase 5 §5.9 Option A).
  _es = new EventSource(buildUrl());

  _es.onopen = () => {
    setStatus('open');
    _backoffMs = BACKOFF_INITIAL_MS;
  };

  _es.onmessage = (ev) => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(ev.data as string);
    } catch {
      return; // ignore malformed
    }
    if (!isProjectorEvent(parsed)) return;
    fanOut(parsed);
  };

  _es.onerror = () => {
    _es?.close();
    _es = null;
    setStatus('error');
    if (_subscribers.size === 0) return; // no one listening; don't auto-reconnect
    _retryTimer = setTimeout(() => {
      if (_subscribers.size > 0) open();
    }, _backoffMs);
    _backoffMs = Math.min(_backoffMs * 2, BACKOFF_MAX_MS);
  };
}

function close(): void {
  if (_retryTimer !== null) {
    clearTimeout(_retryTimer);
    _retryTimer = null;
  }
  _es?.close();
  _es = null;
  setStatus('closed');
}

function subscribe(sub: Subscriber): () => void {
  _subscribers.add(sub);
  // Cancel any pending close on rapid resubscribe.
  if (_closeTimer !== null) {
    clearTimeout(_closeTimer);
    _closeTimer = null;
  }
  open();
  // Emit current status immediately so the new subscriber doesn't
  // sit in the default 'connecting' state until the next change.
  sub.onStatus(_status);
  return () => {
    _subscribers.delete(sub);
    if (_subscribers.size === 0) {
      // Grace period — if a component remounts within 50ms (StrictMode
      // double-invoke or fast route swap), we keep the connection.
      _closeTimer = setTimeout(() => {
        if (_subscribers.size === 0) close();
        _closeTimer = null;
      }, 50);
    }
  };
}

function reconnect(): void {
  // External reconnect — drop the connection and let subscribe()
  // re-open via the existing flow.
  if (_subscribers.size === 0) return;
  if (_es) {
    _es.close();
    _es = null;
  }
  _backoffMs = BACKOFF_INITIAL_MS;
  open();
}

// ─── Hook ─────────────────────────────────────────────────────────

/**
 * Subscribe to the projector's SSE event stream.
 *
 * @param filter  Optional entity-key and/or type-prefix filter applied
 *                client-side. The underlying EventSource is shared
 *                across all useEventStream() consumers on the page.
 *
 * Auth: cookie-based (privy-token). EventSource sends cookies
 * automatically on same-origin requests. Dev-bypass mode skips the
 * cookie check on the projector side.
 */
export function useEventStream(filter?: EventStreamFilter): UseEventStreamResult {
  const [events, setEvents] = useState<ProjectorEvent[]>([]);
  const [status, setStatus] = useState<EventStreamStatus>(_status);

  // Refs for changing-but-shouldn't-restart-effect values.
  const filterRef = useRef(filter);
  filterRef.current = filter;

  useEffect(() => {
    const sub: Subscriber = {
      id: ++_nextSubscriberId,
      filter: filterRef.current ?? {},
      onEvent: (evt) => {
        setEvents((prev) => {
          const next = [...prev, evt];
          return next.length > MAX_EVENTS ? next.slice(next.length - MAX_EVENTS) : next;
        });
      },
      onStatus: setStatus,
    };
    return subscribe(sub);
  }, []); // mount-only; manager re-evaluates filter via the closure

  return { events, status, reconnect };
}

// ─── Test-only inspectors (named with _ prefix to discourage runtime use) ───

/** @internal */
export function _sseManagerSnapshot(): {
  subscriberCount: number;
  hasEventSource: boolean;
  status: EventStreamStatus;
} {
  return {
    subscriberCount: _subscribers.size,
    hasEventSource: _es !== null,
    status: _status,
  };
}

/** @internal — fully reset the manager between tests. */
export function _sseManagerReset(): void {
  if (_retryTimer !== null) clearTimeout(_retryTimer);
  if (_closeTimer !== null) clearTimeout(_closeTimer);
  _retryTimer = null;
  _closeTimer = null;
  _es?.close();
  _es = null;
  _status = 'closed';
  _backoffMs = BACKOFF_INITIAL_MS;
  _subscribers.clear();
}

/** @internal — inject events into the bus from tests without a real EventSource. */
export function _sseManagerEmit(evt: ProjectorEvent): void {
  fanOut(evt);
}

// ─── Type guard ───────────────────────────────────────────────────

function isProjectorEvent(v: unknown): v is ProjectorEvent {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.type === 'string' &&
    typeof o.entity === 'string' &&
    typeof o.timestamp === 'string' &&
    typeof o.eventId === 'string' &&
    typeof o.entityType === 'string' &&
    typeof o.entityId === 'string'
  );
}
